import { useMemo, useState } from 'react';
import PageHeader from '../components/layout/PageHeader';
import { FormField } from '../components/shared/form';
import { useToast } from '../contexts/ToastContext';
import {
  classifyChangePasswordError,
  useChangePassword,
} from '../hooks/useChangePassword';

/**
 * Security — change your password (task 632).
 *
 * The first in-app path to a password change: today the only route is the
 * identity provider's own console, which Users never see. Credentials stay in
 * Keycloak — this form posts to innovate-service `POST /api/users/me/password`,
 * which asks AMS to verify the current password (Keycloak direct grant) and set
 * the new one (Keycloak admin reset). Innovate never stores the password.
 *
 * Two behaviours worth knowing when reading this file:
 *
 *  1. The password policy is Keycloak's, and Keycloak is authoritative. The hint
 *     text mirrors the policy for a fast local check, but a policy rejection
 *     comes back from the server carrying Keycloak's own message, which is shown
 *     against the New password field rather than a guessed one.
 *  2. The write endpoint is not built yet (task 632 backend steps are unchecked).
 *     Until it ships, `CHANGE_PASSWORD_BACKEND_READY` keeps the live call dark:
 *     the form renders with a notice and the submit is disabled, so the page can
 *     ship behind its flag without a dead request. The `backendReady` prop
 *     overrides the gate (tests exercise the full submit path).
 *
 * 2FA and active sessions are deliberately excluded — backlog tasks 638/639 —
 * and will slot in later as additional cards on this page.
 */

/**
 * Flip to true (or wire a flag) when innovate-service `POST /api/users/me/password`
 * and its AMS endpoint ship. Until then the submit is inert so no dead call fires.
 */
export const CHANGE_PASSWORD_BACKEND_READY = false;

/** Keycloak's password policy, mirrored for a fast client-side check. */
const MIN_PASSWORD_LENGTH = 12;
const POLICY_HINT = 'At least 12 characters, with one number and one symbol.';

const hasNumber = (value: string) => /[0-9]/.test(value);
const hasSymbol = (value: string) => /[^A-Za-z0-9]/.test(value);

/** A local check mirroring the policy hint. Keycloak stays the authority. */
const localPolicyError = (value: string): string | undefined => {
  if (value.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (!hasNumber(value)) return 'Include at least one number.';
  if (!hasSymbol(value)) return 'Include at least one symbol.';
  return undefined;
};

interface SecurityPageProps {
  /** Override the backend-ready gate — tests pass `true` to exercise the submit. */
  backendReady?: boolean;
}

export default function SecurityPage({
  backendReady = CHANGE_PASSWORD_BACKEND_READY,
}: SecurityPageProps) {
  const toast = useToast();
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Per-field messages: 'submitted' turns validation on only after a submit
  // attempt, so the User is not scolded while first typing.
  const [submitted, setSubmitted] = useState(false);
  // A server rejection tied to a specific field (wrong current password, or a
  // policy violation carrying Keycloak's message). Cleared as the User edits.
  const [serverFieldError, setServerFieldError] =
    useState<{ field: 'current' | 'new'; message: string } | null>(null);
  // A form-level failure (rate-limited, or an unclassifiable error).
  const [formError, setFormError] = useState<string | null>(null);

  const confirmError = useMemo(() => {
    if (!confirmPassword) return undefined;
    return newPassword !== confirmPassword ? 'The new passwords do not match.' : undefined;
  }, [newPassword, confirmPassword]);

  const newPasswordLocalError = useMemo(
    () => (newPassword ? localPolicyError(newPassword) : undefined),
    [newPassword],
  );

  // Field errors shown to the User: submitted-gated local checks, plus any
  // server rejection pinned to that field.
  const currentError =
    (serverFieldError?.field === 'current' && serverFieldError.message) ||
    (submitted && !currentPassword ? 'Enter your current password.' : undefined) ||
    undefined;

  const newError =
    (serverFieldError?.field === 'new' && serverFieldError.message) ||
    (submitted ? newPasswordLocalError : undefined) ||
    undefined;

  const confirmFieldError =
    (submitted && !confirmPassword ? 'Re-enter your new password.' : undefined) ||
    confirmError ||
    undefined;

  const formFilled =
    currentPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0;

  const clientValid =
    formFilled && !newPasswordLocalError && !confirmError && newPassword !== currentPassword;

  const editField =
    (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      // Any edit clears stale server-side verdicts and the form notice.
      setServerFieldError(null);
      setFormError(null);
    };

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setSubmitted(false);
    setServerFieldError(null);
    setFormError(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setServerFieldError(null);
    setFormError(null);

    if (!backendReady || changePassword.isPending) return;

    // A last local guard: matching, present, and policy-satisfying. Keycloak is
    // still the authority — this only avoids an obviously-doomed round trip.
    if (!formFilled || confirmError || newPasswordLocalError) return;
    if (newPassword === currentPassword) {
      setServerFieldError({
        field: 'new',
        message: 'Choose a password different from your current one.',
      });
      return;
    }

    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          resetForm();
          toast.success('Password changed', 'Your new password is now in effect.');
        },
        onError: (error) => {
          const failure = classifyChangePasswordError(error);
          if (failure.kind === 'invalid_current') {
            setServerFieldError({ field: 'current', message: failure.message });
          } else if (failure.kind === 'policy') {
            setServerFieldError({ field: 'new', message: failure.message });
          } else {
            setFormError(failure.message);
          }
        },
      },
    );
  };

  const submitDisabled = !backendReady || !clientValid || changePassword.isPending;

  return (
    <div>
      <PageHeader
        title="Security"
        description="Manage how you sign in to Monarch Works."
      />

      <div className="mt-6 max-w-2xl">
        <form
          onSubmit={handleSubmit}
          data-testid="security-password-form"
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6"
        >
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Change password
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Passwords are managed by the Monarch Works sign-in service.
          </p>

          {!backendReady && (
            <div
              data-testid="security-backend-notice"
              className="mt-4 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
            >
              In-app password change is being enabled and will be available shortly.
            </div>
          )}

          <div className="mt-5 space-y-5">
            <FormField
              label="Current password"
              name="currentPassword"
              type="password"
              value={currentPassword}
              onChange={editField(setCurrentPassword)}
              error={currentError}
              disabled={!backendReady || changePassword.isPending}
              className="max-w-sm"
            />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <FormField
                  label="New password"
                  name="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={editField(setNewPassword)}
                  error={newError}
                  disabled={!backendReady || changePassword.isPending}
                />
                {!newError && (
                  <p
                    className="mt-1 text-sm text-gray-500 dark:text-gray-400"
                    data-testid="security-policy-hint"
                  >
                    {POLICY_HINT}
                  </p>
                )}
              </div>

              <FormField
                label="Confirm new password"
                name="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={editField(setConfirmPassword)}
                error={confirmFieldError}
                disabled={!backendReady || changePassword.isPending}
              />
            </div>
          </div>

          {formError && (
            <p
              className="mt-4 text-sm text-red-600 dark:text-red-400"
              data-testid="security-form-error"
            >
              {formError}
            </p>
          )}

          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitDisabled}
              data-testid="security-submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {changePassword.isPending ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
